import type { ValueBase } from '@react-types/shared';
import { RefObject, useCallback, useEffect, useRef } from 'react';

interface PseudoInputTarget {
	value: string;
	name: string;
	type: string;
	checked: boolean;
	files: FileList | null;
}

interface PseudoTextAreaTarget {
	value: string;
	name: string;
	type: string;
}

export type OnChangePseudoEvent<E extends HTMLInputElement | HTMLTextAreaElement = HTMLInputElement> =
	E extends HTMLInputElement
		? {
				currentTarget: PseudoInputTarget;
				target: PseudoInputTarget;
		  }
		: {
				currentTarget: PseudoTextAreaTarget;
				target: PseudoTextAreaTarget;
		  };

export interface AccessibleOnChange<E extends HTMLInputElement | HTMLTextAreaElement = HTMLInputElement> {
	(e: OnChangePseudoEvent<E>): void;
}

export function useAccessibleOnChange<E extends HTMLInputElement | HTMLTextAreaElement>(
	onChange: AccessibleOnChange<E>,
	name: string,
	type: Exclude<string, 'checkbox'>,
	ref?: RefObject<E | null>,
): Exclude<ValueBase<string>['onChange'], undefined>;
export function useAccessibleOnChange<E extends HTMLInputElement>(
	onChange: AccessibleOnChange<E>,
	name: string,
	type: 'checkbox',
	ref?: RefObject<E | null>,
): Exclude<ValueBase<boolean>['onChange'], undefined>;
export function useAccessibleOnChange<E extends HTMLInputElement | HTMLTextAreaElement>(
	onChange: AccessibleOnChange<E>,
	name: string,
	type: string,
	ref?: RefObject<E | null>,
): Exclude<ValueBase<string | boolean>['onChange'], undefined> {
	const savedOnChange = useRef<AccessibleOnChange<E>>(onChange);
	useEffect(() => {
		savedOnChange.current = onChange;
	});
	const handleChange = useCallback(
		(_value: string | boolean) => {
			const checked = typeof _value === 'string' ? false : _value;
			const value = typeof _value === 'boolean' ? name : _value;
			const target = ref?.current ?? {
				value,
				name,
				type,
				checked,
				files: null,
			};
			const event = { target, currentTarget: target } as OnChangePseudoEvent<E>;
			savedOnChange.current(event);
		},
		[name, type, ref],
	);
	return handleChange;
}
